<template>
    <div class="animation-input-outer-wrap">
      <div class="animation-input-wrap">
        <div class="animated-input">
          <input 
            :type="inputType" 
            @change="updateParentValue" 
            :value="modelValue" 
            @input="handleInput"
            :id="id"
            v-bind="$attrs"
            required>
          <label :for="props.id" :class="['animated-label']">
            <span v-html="label" :class="['animated-label-content', modelValue === '' ? 'empty-label' : '']"></span>
          </label>
          <span class="underscore"></span>
      <span class="underscore_pw" :class="passwordStrengthClass"></span>
        </div>
      </div>
      <div class="additional-field">
        <span v-if="props.type === 'email'" :class="faClass" class="additional-fontawesome"></span>
        <span @click="togglePasswordVisibility()" v-else-if="props.type === 'password_strength'" 
          :class="['additional-fontawesome', showPassword ? 'eye-visible' : 'eye-hidden']">
        </span>
      </div>

    </div>
</template>

  
  <script setup>
  import { ref, computed } from 'vue';
  
  const props = defineProps({
    label: String,
    type: String,
    modelValue: String,
    id: {
    type: String,
    required: true
  }
  });
  
const faClass = ref('');
const passwordStrengthClass = ref('');

function passwordStrength(pw) {
  return /.{8,}/.test(pw) * (
    /.{12,}/.test(pw)
    + /[a-z]/.test(pw)
    + /[A-Z]/.test(pw)
    + /\d/.test(pw)
    + /[^A-Za-z0-9]/.test(pw)
  );
}


  const emit = defineEmits(['update:modelValue']);
  
  function handleInput(event) {
    const inputValue = event.target.value;
    emit('update:modelValue', inputValue);
    if(props.type === 'password_strength')
    {
      const strength = passwordStrength(event.target.value);
      switch (strength) {
        case 0:
          passwordStrengthClass.value = 'pw-zero';
          break;
        case 1:
          passwordStrengthClass.value = 'pw-bad';
          break;
        case 2:
          passwordStrengthClass.value = 'pw-ok';
          break;
        case 3:
          passwordStrengthClass.value = 'pw-good';
          break;
        case 4:
          passwordStrengthClass.value =  'pw-very-good';
          break;
        case 5:
          passwordStrengthClass.value =  'pw-perfect';
          break;
        default:
          console.error("getLabelClass Error");
          break;
      }
      console.log("newpwclass", passwordStrengthClass.value);
    }
    else if (props.type === 'email') 
    {
      console.log("inputValue", inputValue);

        // Check if the inputValue is empty
      if (!inputValue) {
        faClass.value = '';
      } else {
        faClass.value = validateEmail(inputValue) ? 'checkmark' : 'cross';
      }
    }
    else if (props.type === 'password') 
    {
      // Caps Lock
    }

  }

function togglePasswordVisibility() {
    showPassword.value = !showPassword.value;
}

function validateEmail(email) {
    const mailformat = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]{2,})+$/;
    return mailformat.test(email);
}

// Data
const content = ref("");
const className = computed(() => props.label.replace(" ", "-").toLowerCase());
const showPassword = ref(false);
const inputType = computed(() => {
    if ((props.type === 'password' || props.type === 'password_strength') && !showPassword.value) {
        return 'password';
    }
    return 'text';
});



// Lifecycle hooks
onMounted(() => {
  if(props.variable) content.value = props.variable.data;
});

</script>

<style lang="scss" scoped>
@import '~/assets/constants.scss';

/* Base underscore */
.underscore {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: gray; /* Or any color you want for the base */
    z-index: 1;
}

/* Password strength underscore */
.underscore_pw {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 1px;
    z-index: 2; /* Ensures it's above the base underscore */
}

/* Password strength colors and widths */
.pw-zero {
    width: 0; /* No strength */
    background-color: transparent; 
}

.pw-bad {
    width: 20%;
    background-color: red;
}

.pw-ok {
    width: 40%;
    background-color: orange;
}

.pw-good {
    width: 60%;
    background-color: yellow;
}

.pw-very-good {
    width: 80%;
    background-color: lightgreen;
}

.pw-perfect {
    width: 100%; 
    background-color: green;
}


.eye-visible::after {
   content: "\f06e";  // eye icon
   color: $fa-dark-gray;
}

.eye-hidden::after {
   content: "\f070";  // eye-slash icon
   color: $fa-gray;
}


.additional-fontawesome input {
    opacity: 0;
    width: 0;
    height: 0;
}

.additional-fontawesome {
    position: relative;
    width: 45px;
    grid-row: 1;
    grid-column: 1;  
    border-radius: $fa-border-radius; 
    align-content: center;
    justify-content: center;
    border: none !important;
    display: grid;
    padding-top: 1.0rem;
}

.additional-field {
    display: grid;
    justify-content: center;
    align-items: center;
}

.additional-checkbox {
    grid-row: 1;
    grid-column: 1;
    opacity: 0;
    width: 100%;
    height: 100%;
}

.additional-fontawesome::after,
.checkmark::after,
.cross::after {
    font-family: 'FontAwesome';
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    line-height: 1;
    -webkit-font-smoothing: antialiased;
    //color: transparent;
    font-size: 1.5rem;
}

.cross::after {
    content: "\f057";
    color: red;
}

.dark-mode input:checked + .pw-eye::after {
   content: "\f06e";
   color: $light-white;
}

input:checked + .pw-eye::after {
   content: "\f06e";
   color: #444;
}

.checkmark::after {
   content: "\f058";
   color: green;
}

.dark-mode .checkmark::after {   
    content: "\f00c";    
}

.pw-false::after {
   content: "\f057";
   color: red;
}

.dark-mode .pw-false::after {
   content: "\f00d";
   color: red;
}

.caps-lock::after{
    color: red;
    content: '⇪';
}

.animation-input-wrap {
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-direction: column;
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
}

.animated-input {
    width: 100%;
    position: relative;
    //overflow: hidden;
    padding-top: 0.9rem;
}

.animated-input input {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    box-shadow: none;
}

.animated-input input:focus + .animated-label::after,
.animated-input input:valid + .animated-label::after {
    transform: translateX(0%);
}

label {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 100%;
    width: 100%;
    pointer-events: none;
    //border-bottom: 1px solid #444;
    font-size: 1.25rem;
}

.dark-mode label {
    border-bottom: 1px solid $light-white;
    color: #ccc;
}

input {
    background: none;
    color: gray;
    transform: translateY(0s);
    font-family: 'Glacial Indifference', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    font-size: 1.25rem;
}

.dark-mode input {
    color: $light-white;
    text-shadow: 1px 1px 1px #444;
}


.animated-input label::after,
.pw-zero::after,
.pw-bad::after,
.pw-ok::after,
.pw-good::after,
.pw-very-good::after,
.pw-perfect::after {
    content: "";
    position: absolute;
    height: 100%;
    width: 100%;
    bottom: -1px;
    left: 0;
    transform: translateX(-100%);
    transition: all 0.3s ease;
    display: none;
}

.animated-label-content {
    position: absolute;
    bottom: 0px;
    left: 0;
    transition: all 0.3s ease;
    transform: translateY(-130%);
    font-size: 1.0rem;
    padding-left: 0.125rem;
    color: #999;
}

input:focus + .animated-label .animated-label-content,
input:valid + .animated-label .animated-label-content {
    transform: translateY(-130%);
    font-size: 1.0rem;
    color: #999;
}

.empty-label {
    transform: translateY(0%);
    font-size: inherit;
    color: gray;
}

.animation-input-outer-wrap {
    display: grid;
    grid-template-columns: 10fr 1fr;
}

</style>
